/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "antd/dist/reset.css";
import { SessionProvider } from "next-auth/react";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import "nprogress/nprogress.css";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { FIVE_MINUTES_MS, TWO_MINUTES_MS } from "lib/constants";
import { ROBOTS_DIRECTIVE } from "lib/searchIndexing";
import {
  isUnauthorizedError,
  handleUnauthorizedError,
} from "../lib/errors/errorHandler";
import { CURRENT_VERSION } from "../lib/util";
import { hydrateTenantSettingsStoreFromBundled } from "../lib/tenantSettings/hydrate";
import { useTenantDiscovery } from "../lib/tenantDiscovery";
import SentryContextUpdater from "../components/SentryContextUpdater";
import TenantOverrideBanner from "../components/TenantOverrideBanner";
import TenantDiscoveryGate from "../components/TenantDiscoveryGate";
import { getCentralAuthHostClient } from "../lib/auth/centralAuthClient";
import { syncTenantOverrideCookie } from "../config/tenants";
import { useTenantOverrideBranding } from "../lib/tenantOverride";

// ATT-2871: synchronously hydrate the tenant settings store from bundled config
// BEFORE any component renders. Guarantees `tenantHasFlag()` and other store
// readers see valid data on first render — no race window. SSR no-op (window
// guard); on the server, helpers fall back to bundled lookups directly.
if (typeof window !== "undefined") {
  hydrateTenantSettingsStoreFromBundled();
}
import "../styles/global.css";
import "../styles/fullcalendar.css";
import "../styles/datepicker-mobile.css";
// CNS report pages: hide LayoutDashboard chrome when printing (must be global CSS).
import "../styles/attestation-report-print.css";

export default function App({ Component, pageProps }: AppProps) {
  // ATT-3464: the central auth host is not a tenant, so host-keyed discovery
  // resolves UNKNOWN_DOMAIN there and the gate below would trap the user in the
  // retry screen. `/auth/start` is the only page served on that host; it
  // validates the origin tenant server-side and hands it over here, so branding
  // resolves for the DISTRICT the user came from rather than for the auth host.
  //
  // Both values are inert while `NEXT_PUBLIC_CENTRAL_AUTH_HOST` is unset:
  // `getCentralAuthHostClient()` returns undefined (so `onCentralAuthHost` is
  // false on every host) and `/auth/start` refuses before it can set the prop.
  const centralAuthOriginHost =
    typeof pageProps?.centralAuthOriginHost === "string"
      ? pageProps.centralAuthOriginHost
      : undefined;
  const centralAuthHost = getCentralAuthHostClient();
  const onCentralAuthHost =
    Boolean(centralAuthHost) &&
    typeof window !== "undefined" &&
    window.location.host.toLowerCase() === centralAuthHost;

  // ATT-3383: fire tenant discovery once at app boot for EVERY host (tenant
  // resolution is registry-only — no CSV allowlist gate). The hook reads from
  // `useTenantDiscoveryStore` so the discovered tenant identity (display name,
  // hide languages, feature flags, logo URL) and routing become available to
  // any consumer that subscribes to that store. `source` + `retry` drive the
  // loading/retry gate below: on discovery failure the app shows an explicit
  // retry state instead of a silent empty-branding render.
  const {
    source: tenantDiscoverySource,
    slow: tenantDiscoverySlow,
    retry: retryTenantDiscovery,
  } = useTenantDiscovery(centralAuthOriginHost);

  // ATT-3384: when a staging tenant-override is active (gated by
  // NEXT_PUBLIC_ALLOW_TENANT_OVERRIDE), fetch the target's branding from the
  // backend so the override simulates that tenant's look while auth/data stay
  // pinned to the real staging tenant. Inert in production (gate off → no fetch).
  useTenantOverrideBranding();

  // ATT-3226: persist a `?__tenant=<host>` deep-link into the
  // `attendly_tenant_override` session cookie (or clear it on `?__tenant=clear`)
  // so the override survives subsequent navigations. Gate-guarded inside the
  // helper — a no-op unless NEXT_PUBLIC_ALLOW_TENANT_OVERRIDE === "true".
  // First-paint branding fidelity comes from the query-param-first read in
  // getTenantOverrideHost (design.md §D6), not from this effect.
  useEffect(() => {
    syncTenantOverrideCookie();
  }, []);

  // ATT-3024: stable QueryClient instance — one per App mount, not per render.
  // Recreating it on every render wiped the cache and orphaned mutation
  // onSuccess closures, so invalidateQueries hit a discarded client and
  // active queries never refetched. Lazy useState matches the TanStack +
  // Next.js Pages Router recommended pattern.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            cacheTime: FIVE_MINUTES_MS,
            staleTime: TWO_MINUTES_MS,
            retry: false,
            onError: async (error: any) => {
              if (isUnauthorizedError(error)) {
                await handleUnauthorizedError();
              }
            },
          },
        },
      })
  );

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/version");
        const data = await res.json();

        if (
          data &&
          typeof data.version === "string" &&
          data.version !== CURRENT_VERSION
        ) {
          // ATT-3908 — never reload out from under a live bulk lottery run
          // (admin-tools/lottery.tsx raises this flag while one is in
          // flight). The next 15-minute tick picks the update up instead.
          if ((window as Window & { __attendlyHoldAutoReload?: boolean }).__attendlyHoldAutoReload) {
            // eslint-disable-next-line no-console
            console.log("> New version available, but a bulk run is in flight — holding reload");
            return;
          }
          // eslint-disable-next-line no-console
          console.log("> New version available, reloading page", {
            dataVersion: data.version,
            currentVersion: CURRENT_VERSION,
          });
          window.location.reload();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to check for updates", error);
      }
    }, 900_000); // Check every 900 seconds / 15 minutes

    return () => clearInterval(interval);
  }, []);

  const ClarityInit = dynamic(() => import("../components/ClarityInit"), {
    ssr: false,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider session={pageProps.session}>
        <Head>
          <meta name="robots" content={ROBOTS_DIRECTIVE} />
        </Head>
        <SentryContextUpdater />
        <TenantDiscoveryGate
          source={tenantDiscoverySource}
          slow={tenantDiscoverySlow}
          onRetry={retryTenantDiscovery}
          isCentralAuthHost={onCentralAuthHost}
        >
          <Component {...pageProps} />
        </TenantDiscoveryGate>
        <TenantOverrideBanner />
        <ClarityInit />
      </SessionProvider>

      {/* {process.env.NODE_ENV === "development" && isLocalhost() && (
        <ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
      )} */}

      <Script id="clarity-script" strategy="afterInteractive">
        {`
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID}");
        `}
      </Script>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GTM_ID}`}
      />
      <Script id="gtm-script" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${process.env.NEXT_PUBLIC_GTM_ID}');
        `}
      </Script>
    </QueryClientProvider>
  );
}
